import { useState, useEffect } from 'react';
import { useUser } from '@/context/UserContext';
import { httpsCallable } from 'firebase/functions';
import { functions } from '@/config/firebase';
import '@/styles/AdminPanel.css';
import { useNavigate } from 'react-router-dom';

const getGameSettingsCallable = httpsCallable(functions, 'getGameSettings');
const updateGameSettingsCallable = httpsCallable(functions, 'updateGameSettings');
const getAdminUserDataCallable = httpsCallable(functions, 'getAdminUserData');
const updateAdminUserDataCallable = httpsCallable(functions, 'updateAdminUserData');
const getTasksCallable = httpsCallable(functions, 'getTasks');
const adminUpsertTaskCallable = httpsCallable(functions, 'adminUpsertTask');
const adminDeleteTaskCallable = httpsCallable(functions, 'adminDeleteTask');
const adminReorderTasksCallable = httpsCallable(functions, 'adminReorderTasks');

type AdminToast = {
    type: 'success' | 'error';
    message: string;
} | null;

const AdminPanel = () => {
    const { user, loading: userLoading } = useUser();
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState<'settings' | 'users' | 'tasks'>('settings');
    const [loading, setLoading] = useState(false);

    // Settings State
    const [settings, setSettings] = useState<any>({});

    // Users State
    const [users, setUsers] = useState<any[]>([]);
    const [search, setSearch] = useState('');
    const [lastDocId, setLastDocId] = useState<string | null>(null);

    // Edit Modal State
    const [editingUser, setEditingUser] = useState<any | null>(null);
    const [editData, setEditData] = useState<any>({});
    const [tasks, setTasks] = useState<any[]>([]);
    const [editingTask, setEditingTask] = useState<any>({
        id: '',
        title: '',
        reward: 0,
        link: '',
        status: 'available',
        order: 9999,
        claimDelaySeconds: 0,
    });
    const [dragTaskId, setDragTaskId] = useState<string | null>(null);
    const [toast, setToast] = useState<AdminToast>(null);

    const showToast = (type: 'success' | 'error', message: string) => {
        setToast({ type, message });
        window.setTimeout(() => setToast(null), 3000);
    };

    useEffect(() => {
        if (!userLoading) {
            if (!user?.isAdmin) {
                navigate('/');
            } else {
                fetchSettings();
            }
        }
    }, [user, userLoading, navigate]);

    const fetchSettings = async () => {
        setLoading(true);
        try {
            const res = await getGameSettingsCallable();
            setSettings(res.data);
        } catch (e) {
            console.error("Failed to fetch settings", e);
            showToast('error', 'Error loading settings');
        } finally {
            setLoading(false);
        }
    };

    const saveSettings = async () => {
        if (!confirm("Are you sure you want to update global game settings?")) return;
        setLoading(true);
        try {
            await updateGameSettingsCallable(settings);
            showToast('success', 'Settings updated');
        } catch (e) {
            console.error("Failed to save settings", e);
            showToast('error', 'Error saving settings');
        } finally {
            setLoading(false);
        }
    };

    const fetchUsers = async (reset = false) => {
        setLoading(true);
        try {
            const currentLastDoc = reset ? null : lastDocId;
            const res = await getAdminUserDataCallable({
                pageSize: 20,
                lastDocId: currentLastDoc,
                search: search.trim() || null
            });
            const data: any = res.data;

            if (reset) {
                setUsers(data.users);
            } else {
                setUsers(prev => [...prev, ...data.users]);
            }
            setLastDocId(data.lastDocId);
        } catch (e) {
            console.error("Failed to fetch users", e);
            showToast('error', 'Error fetching users');
        } finally {
            setLoading(false);
        }
    };

    const fetchTasks = async () => {
        setLoading(true);
        try {
            const res: any = await getTasksCallable();
            setTasks(res.data?.tasks || []);
        } catch (e) {
            console.error("Failed to fetch tasks", e);
            showToast('error', 'Error fetching tasks');
        } finally {
            setLoading(false);
        }
    };

    const openEditModal = (u: any) => {
        setEditingUser(u);
        setEditData({
            points: u.points || 0,
            bananium: u.bananium || 0,
            token404: u.token404 || 0,
            isAdmin: u.isAdmin || false,
            username: u.username || ''
        });
    };

    const saveUserChanges = async () => {
        if (!editingUser) return;
        setLoading(true);
        try {
            await updateAdminUserDataCallable({
                userId: editingUser.id,
                data: editData
            });
            showToast('success', 'User updated');
            setEditingUser(null);
            // Refresh list specifically for this user or reload all
            setUsers(prev => prev.map(u => u.id === editingUser.id ? { ...u, ...editData } : u));
        } catch (e) {
            console.error("Failed to update user", e);
            showToast('error', 'Error updating user');
        } finally {
            setLoading(false);
        }
    };

    const saveTask = async () => {
        setLoading(true);
        try {
            await adminUpsertTaskCallable({
                taskId: editingTask.id || undefined,
                task: {
                    title: editingTask.title,
                    reward: Number(editingTask.reward),
                    link: editingTask.link,
                    status: editingTask.status,
                    order: Number(editingTask.order),
                    claimDelaySeconds: Number(editingTask.claimDelaySeconds || 0),
                },
            });
            showToast('success', 'Task saved');
            setEditingTask({ id: '', title: '', reward: 0, link: '', status: 'available', order: 9999, claimDelaySeconds: 0 });
            await fetchTasks();
        } catch (e) {
            console.error("Failed to save task", e);
            showToast('error', 'Error saving task');
        } finally {
            setLoading(false);
        }
    };

    const editTask = (task: any) => {
        setEditingTask({
            id: task.id || '',
            title: task.title || '',
            reward: Number(task.reward || 0),
            link: task.link || '',
            status: task.status || 'available',
            order: Number(task.order ?? 9999),
            claimDelaySeconds: Number(task.claimDelaySeconds ?? 0),
        });
    };

    const deleteTask = async (taskId: string) => {
        if (!confirm('Delete this task?')) return;
        setLoading(true);
        try {
            await adminDeleteTaskCallable({ taskId });
            showToast('success', 'Task deleted');
            await fetchTasks();
        } catch (e) {
            console.error("Failed to delete task", e);
            showToast('error', 'Error deleting task');
        } finally {
            setLoading(false);
        }
    };

    const toggleTaskStatus = async (task: any) => {
        const nextStatus = task.status === 'available' ? 'paused' : 'available';
        setLoading(true);
        try {
            await adminUpsertTaskCallable({
                taskId: task.id,
                task: {
                    title: task.title,
                    reward: Number(task.reward),
                    link: task.link,
                    status: nextStatus,
                    order: Number(task.order ?? 9999),
                    claimDelaySeconds: Number(task.claimDelaySeconds ?? 0),
                },
            });
            showToast('success', `Task ${nextStatus}`);
            await fetchTasks();
        } catch (e) {
            console.error("Failed to toggle task status", e);
            showToast('error', 'Error updating task status');
        } finally {
            setLoading(false);
        }
    };

    const handleTaskDrop = async (targetTaskId: string) => {
        if (!dragTaskId || dragTaskId === targetTaskId) return;
        const sourceIndex = tasks.findIndex((t) => t.id === dragTaskId);
        const targetIndex = tasks.findIndex((t) => t.id === targetTaskId);
        if (sourceIndex < 0 || targetIndex < 0) return;

        const next = [...tasks];
        const [moved] = next.splice(sourceIndex, 1);
        next.splice(targetIndex, 0, moved);
        setTasks(next);
        setDragTaskId(null);

        try {
            await adminReorderTasksCallable({ orderedTaskIds: next.map((t) => t.id) });
            showToast('success', 'Task order updated');
            await fetchTasks();
        } catch (e) {
            console.error("Failed to reorder tasks", e);
            showToast('error', 'Failed to reorder tasks');
        }
    };

    // Render helpers
    const handleSettingChange = (key: string, val: any) => {
        setSettings((prev: any) => ({ ...prev, [key]: val }));
    };

    const loadedUsersCount = users.length;
    const loadedAdminsCount = users.filter((u) => Boolean(u.isAdmin)).length;
    const loadedTotalPoints = users.reduce((sum, u) => sum + (Number(u.points) || 0), 0);
    const loadedTotalBuink = users.reduce((sum, u) => sum + (Number(u.token404) || 0), 0);

    if (userLoading || !user?.isAdmin) return <div className="admin-loading">Checking permissions...</div>;

    return (
        <div className="admin-root">
            {toast && (
                <div className={`admin-toast ${toast.type === 'success' ? 'success' : 'error'}`}>
                    {toast.message}
                </div>
            )}
            <div className="admin-header">
                <h1>Admin Panel</h1>
                <p>Manage economy settings and user access.</p>
            </div>

            <div className="admin-kpis">
                <div className="admin-kpi-card">
                    <span className="kpi-label">Loaded Users</span>
                    <span className="kpi-value">{loadedUsersCount}</span>
                </div>
                <div className="admin-kpi-card">
                    <span className="kpi-label">Loaded Admins</span>
                    <span className="kpi-value">{loadedAdminsCount}</span>
                </div>
                <div className="admin-kpi-card">
                    <span className="kpi-label">Loaded Points</span>
                    <span className="kpi-value">{loadedTotalPoints}</span>
                </div>
                <div className="admin-kpi-card">
                    <span className="kpi-label">Loaded Buink</span>
                    <span className="kpi-value">{loadedTotalBuink}</span>
                </div>
            </div>

            <div className="admin-tabs">
                <button onClick={() => setActiveTab('settings')} className={activeTab === 'settings' ? 'active' : ''}>Game Settings</button>
                <button onClick={() => { setActiveTab('users'); fetchUsers(true); }} className={activeTab === 'users' ? 'active' : ''}>User Management</button>
                <button onClick={() => { setActiveTab('tasks'); fetchTasks(); }} className={activeTab === 'tasks' ? 'active' : ''}>Tasks</button>
            </div>

            {activeTab === 'settings' && (
                <div className="admin-card">
                    <h3>Economy</h3>
                    <div className="admin-form-group">
                        <label>Island Cost (TON)</label>
                        <input type="number" value={settings.islandCostTon || ''} onChange={e => handleSettingChange('islandCostTon', Number(e.target.value))} />
                    </div>
                    <div className="admin-form-group">
                        <label>Island Cost (Buink)</label>
                        <input type="number" value={settings.islandCostBuink || ''} onChange={e => handleSettingChange('islandCostBuink', Number(e.target.value))} />
                    </div>
                    <h3>Point Packs (JSON)</h3>
                    <textarea
                        className="admin-json-input"
                        value={JSON.stringify(settings.pointPacks || [], null, 2)}
                        onChange={e => {
                            try {
                                handleSettingChange('pointPacks', JSON.parse(e.target.value));
                            } catch (err) {
                                // ignore parse error while typing
                            }
                        }}
                    />
                    <h3>Voxel Packs (JSON)</h3>
                    <textarea
                        className="admin-json-input"
                        value={JSON.stringify(settings.voxelPacks || [], null, 2)}
                        onChange={e => {
                            try {
                                handleSettingChange('voxelPacks', JSON.parse(e.target.value));
                            } catch (err) {
                                // ignore parse error while typing
                            }
                        }}
                    />
                    <button onClick={saveSettings} disabled={loading} className="admin-primary-btn">Save Global Settings</button>
                </div>
            )}

            {activeTab === 'users' && (
                <div className="admin-card users-manager">
                    <div className="admin-search-row">
                        <input
                            type="text"
                            placeholder="Search Wallet, Username, Telegram ID..."
                            value={search}
                            onChange={e => setSearch(e.target.value)}
                        />
                        <button onClick={() => fetchUsers(true)} disabled={loading} className="admin-secondary-btn">Search</button>
                    </div>

                    <div className="admin-table-wrap">
                    <table className="admin-table">
                        <thead>
                            <tr>
                                <th>Username</th>
                                <th>Points</th>
                                <th>Buink</th>
                                <th>Admin?</th>
                                <th>Action</th>
                            </tr>
                        </thead>
                        <tbody>
                            {users.map(u => (
                                <tr key={u.id}>
                                    <td>
                                        {u.username}<br />
                                        <small>{u.id}</small>
                                    </td>
                                    <td>{u.points}</td>
                                    <td>{u.token404}</td>
                                    <td>{u.isAdmin ? '✅' : '❌'}</td>
                                    <td>
                                        <button onClick={() => openEditModal(u)} className="admin-secondary-btn admin-edit-btn">Edit</button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    </div>

                    {lastDocId && (
                        <button onClick={() => fetchUsers(false)} disabled={loading} className="admin-primary-btn admin-load-more">Load More</button>
                    )}
                </div>
            )}

            {activeTab === 'tasks' && (
                <div className="admin-card users-manager">
                    <h3>Task Management</h3>
                    <div className="admin-form-group">
                        <label>Title</label>
                        <input
                            type="text"
                            value={editingTask.title}
                            onChange={(e) => setEditingTask((prev: any) => ({ ...prev, title: e.target.value }))}
                        />
                    </div>
                    <div className="admin-form-group">
                        <label>Reward (Buink)</label>
                        <input
                            type="number"
                            value={editingTask.reward}
                            onChange={(e) => setEditingTask((prev: any) => ({ ...prev, reward: Number(e.target.value) }))}
                        />
                    </div>
                    <div className="admin-form-group">
                        <label>Link</label>
                        <input
                            type="text"
                            value={editingTask.link}
                            onChange={(e) => setEditingTask((prev: any) => ({ ...prev, link: e.target.value }))}
                        />
                    </div>
                    <div className="admin-form-group">
                        <label>Status</label>
                        <select
                            value={editingTask.status}
                            onChange={(e) => setEditingTask((prev: any) => ({ ...prev, status: e.target.value }))}
                        >
                            <option value="available">available</option>
                            <option value="paused">paused</option>
                            <option value="completed">completed</option>
                        </select>
                    </div>
                    <div className="admin-form-group">
                        <label>Order</label>
                        <input
                            type="number"
                            value={editingTask.order}
                            onChange={(e) => setEditingTask((prev: any) => ({ ...prev, order: Number(e.target.value) }))}
                        />
                    </div>
                    <div className="admin-form-group">
                        <label>Claim Delay (seconds)</label>
                        <input
                            type="number"
                            value={editingTask.claimDelaySeconds}
                            onChange={(e) => setEditingTask((prev: any) => ({ ...prev, claimDelaySeconds: Number(e.target.value) }))}
                        />
                    </div>
                    <div className="admin-modal-actions">
                        <button className="admin-primary-btn" onClick={saveTask} disabled={loading}>Save Task</button>
                        <button
                            className="admin-secondary-btn"
                            onClick={() => setEditingTask({ id: '', title: '', reward: 0, link: '', status: 'available', order: 9999, claimDelaySeconds: 0 })}
                        >
                            New Task
                        </button>
                    </div>

                    <div className="admin-table-wrap" style={{ marginTop: 16 }}>
                        <table className="admin-table">
                            <thead>
                                <tr>
                                    <th>Title</th>
                                    <th>Reward</th>
                                    <th>Status</th>
                                    <th>Order</th>
                                    <th>Delay(s)</th>
                                    <th>Action</th>
                                </tr>
                            </thead>
                            <tbody>
                                {tasks.map((task) => (
                                    <tr
                                        key={task.id}
                                        draggable
                                        onDragStart={() => setDragTaskId(task.id)}
                                        onDragOver={(e) => e.preventDefault()}
                                        onDrop={() => handleTaskDrop(task.id)}
                                        className={dragTaskId === task.id ? 'dragging' : ''}
                                    >
                                        <td>{task.title}</td>
                                        <td>{task.reward}</td>
                                        <td>{task.status}</td>
                                        <td>{task.order ?? 9999}</td>
                                        <td>{Number(task.claimDelaySeconds || 0)}</td>
                                        <td>
                                            <button className="admin-secondary-btn admin-edit-btn" onClick={() => editTask(task)}>Edit</button>{' '}
                                            <button className="admin-secondary-btn admin-edit-btn" onClick={() => toggleTaskStatus(task)}>
                                                {task.status === 'available' ? 'Disable' : 'Enable'}
                                            </button>{' '}
                                            <button className="admin-secondary-btn admin-edit-btn" onClick={() => deleteTask(task.id)}>Delete</button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {editingUser && (
                <div className="admin-modal-overlay">
                    <div className="admin-modal-content">
                        <h2>Edit User: {editingUser.username}</h2>

                        <div className="admin-form-group">
                            <label>Username</label>
                            <input type="text" value={editData.username} onChange={e => setEditData({ ...editData, username: e.target.value })} />
                        </div>

                        <div className="admin-form-group">
                            <label>Points</label>
                            <input type="number" value={editData.points} onChange={e => setEditData({ ...editData, points: Number(e.target.value) })} />
                        </div>

                        <div className="admin-form-group">
                            <label>Buink</label>
                            <input type="number" value={editData.token404} onChange={e => setEditData({ ...editData, token404: Number(e.target.value) })} />
                        </div>

                        <div className="admin-form-row">
                            <label>Is Admin?</label>
                            <input type="checkbox" checked={editData.isAdmin} onChange={e => setEditData({ ...editData, isAdmin: e.target.checked })} />
                        </div>

                        <div className="admin-modal-actions">
                            <button onClick={saveUserChanges} disabled={loading} className="admin-primary-btn">Save</button>
                            <button onClick={() => setEditingUser(null)} className="admin-secondary-btn">Cancel</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AdminPanel;
